import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AnonymousAuthService } from './anonymous-auth.service';
import { API_PATHS } from '../config/api-paths';
import { GraceConfig, GraceConfigApi } from '../tokens/grace-config.token';
import { DEFAULT_CARD_NETWORK } from '../util/card-network';

/** Shape of the response from `GET /services/embedded/api/grace-config`. */
interface GraceConfigResponse {
  defaultAccountCurrency: string;
  profile: string;
  cardNetwork: string;
  doNotCreateBankAccounts: boolean;
  overrideKyb: boolean;
  overrideKyc: boolean;
}

/**
 * REST-backed implementation of `GraceConfigApi`.
 *
 * Fetches `GET /services/embedded/api/grace-config` once per app lifetime using
 * the anonymous token (the endpoint is permitAll — anonymous token is sufficient
 * and allows the badge to show before login for staff who reload the shell).
 *
 * On any HTTP or parse error the signals keep their initial fallback values
 * (`defaultAccountCurrency = 'EUR'`, `profile = ''`, `cardNetwork = 'VISA'`) — the Track-2
 * currency defaults to EUR, its card preview keeps the Visa mark, and the badge stays hidden
 * (profile empty → `isVisible` false).
 *
 * Rule 07/10 compliance: REST-only, no Mock* fallback, no Mock* class.
 */
@Injectable({ providedIn: 'root' })
export class GraceConfigService implements GraceConfigApi {
  private readonly http          = inject(HttpClient);
  private readonly anonymousAuth = inject(AnonymousAuthService);

  /** Whether `ensureLoaded()` has already been called. */
  private loaded = false;

  readonly defaultAccountCurrency = signal<string>('EUR');
  readonly profile                = signal<string>('');
  /**
   * EM-1651 — card scheme of the configured card product, from `grace.card-network`. The fallback is
   * the PoC product's scheme (Visa Prepaid Business Reloadable, BIN 4537 4652), so the Track-2 card
   * preview never shows a brand the platform does not issue, even before the response lands.
   */
  readonly cardNetwork            = signal<string>(DEFAULT_CARD_NETWORK);
  readonly doNotCreateBankAccounts = signal<boolean>(true);

  /**
   * Idempotent: the HTTP call is issued at most once per app lifetime.
   * Subsequent calls from any component are no-ops.
   */
  ensureLoaded(): void {
    if (this.loaded) return;
    this.loaded = true;

    this.anonymousAuth.getToken().pipe(
      switchMap(token =>
        this.http.get<GraceConfigResponse>(API_PATHS.GRACE_CONFIG, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ),
      catchError(() => of<GraceConfigResponse | null>(null)),
    ).subscribe(resp => {
      if (!resp) return;
      if (resp.defaultAccountCurrency) {
        this.defaultAccountCurrency.set(resp.defaultAccountCurrency);
      }
      if (resp.profile) {
        this.profile.set(resp.profile);
      }
      if (resp.cardNetwork) {
        this.cardNetwork.set(resp.cardNetwork);
      }
      this.doNotCreateBankAccounts.set(resp.doNotCreateBankAccounts);
    });
  }

  /**
   * Fetch the full Grace configuration (incl. `doNotCreateBankAccounts`) for the
   * Master-Admin Grace Config page. Rides the signed-in master-admin bearer via
   * the authInterceptor (the GET endpoint is permitAll). REST-only, no Mock fallback.
   */
  get(): Observable<GraceConfig> {
    return this.http.get<GraceConfig>(API_PATHS.GRACE_CONFIG).pipe(tap(cfg => this.syncSignals(cfg)));
  }

  /**
   * Update the Grace configuration. Only `doNotCreateBankAccounts` is applied
   * server-side. Authority: ROLE_MASTER_ADMIN. REST-only, no Mock fallback.
   */
  update(config: GraceConfig): Observable<GraceConfig> {
    return this.http.put<GraceConfig>(API_PATHS.GRACE_CONFIG, config).pipe(tap(cfg => this.syncSignals(cfg)));
  }

  /** Keep the shared signals (consumed by the staff-shell status badge) in step with the API. */
  private syncSignals(cfg: GraceConfig): void {
    if (cfg.defaultAccountCurrency) {
      this.defaultAccountCurrency.set(cfg.defaultAccountCurrency);
    }
    if (cfg.profile) {
      this.profile.set(cfg.profile);
    }
    if (cfg.cardNetwork) {
      this.cardNetwork.set(cfg.cardNetwork);
    }
    this.doNotCreateBankAccounts.set(cfg.doNotCreateBankAccounts);
  }
}
